import { useCallback, useState } from 'react';
import { useLockBodyScroll } from '../../hooks/useLockBodyScroll';
import { MobileJourneyPicker } from '../MobileJourneyPicker';
import {
  MobileModalOverlay,
  MobileTrigger,
  MobileTriggerSubtitle,
  MobileTriggerTitle,
} from './JourneyPickerMobile.styles';
import type { JourneyPickerSharedProps } from './JourneyPickerView.types';

export interface JourneyPickerMobileProps extends JourneyPickerSharedProps {
  triggerTitle: string;
  triggerSubtitle?: string;
  closeLabel: string;
}

export function JourneyPickerMobile({
  triggerTitle,
  triggerSubtitle,
  closeLabel,
  imageOverlay = false,
  showCategory,
  categories,
  category,
  onCategorySelect,
  fromDate,
  toDate,
  isSingleDate,
  onRangeSelect,
  displayGuests,
  adults,
  children,
  maxAdults,
  maxChildren,
  onAdultsChange,
  onChildrenChange,
  availability,
  showPastDates,
  onMonthChange,
  onSearch,
  searchLabel,
  datePickerLabels,
  guestPickerLabels,
  showRate,
  rateCode,
  rateLabels,
  onRateCodeChange,
}: JourneyPickerMobileProps) {
  const [open, setOpen] = useState(false);

  useLockBodyScroll(open);

  const close = useCallback(() => setOpen(false), []);

  const handleNext = useCallback(() => {
    close();
    onSearch();
  }, [close, onSearch]);

  return (
    <>
      <MobileTrigger
        type="button"
        data-journey-picker-edit-dates
        data-image-overlay={imageOverlay}
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <MobileTriggerTitle>{triggerTitle}</MobileTriggerTitle>
        {triggerSubtitle && <MobileTriggerSubtitle>{triggerSubtitle}</MobileTriggerSubtitle>}
      </MobileTrigger>

      {open && (
        <MobileModalOverlay role="dialog" aria-modal="true">
          <MobileJourneyPicker
            categories={showCategory ? categories : undefined}
            category={category}
            onCategoryChange={onCategorySelect}
            fromDate={fromDate || null}
            toDate={isSingleDate ? fromDate || null : toDate || null}
            isSingleDate={isSingleDate}
            availability={availability}
            showPastDates={showPastDates}
            onMonthChange={onMonthChange}
            onRangeSelect={onRangeSelect}
            adults={adults}
            childCount={children}
            maxAdults={maxAdults}
            maxChildren={maxChildren}
            onAdultsChange={onAdultsChange}
            onChildrenChange={onChildrenChange}
            showWho={displayGuests}
            defaultOpenSection={showCategory && !category ? 'what' : 'when'}
            onClose={close}
            onNext={handleNext}
            showRate={showRate}
            rateCode={rateCode}
            onRateCodeChange={onRateCodeChange}
            labels={{
              closeAriaLabel: closeLabel,
              next: searchLabel,
              rateCode: rateLabels.placeholder,
              rateApply: rateLabels.apply,
              rateClear: rateLabels.clear,
              calendar: datePickerLabels,
              guestPicker: guestPickerLabels,
            }}
          />
        </MobileModalOverlay>
      )}
    </>
  );
}
